import { createAsyncThunk } from '@reduxjs/toolkit';
import { v4Service, IPaymentsRoute } from '#/api/v4Service';
import { getUrlParams } from '#/util/urlParams';
import { toWalletOptions } from '#/util/walletId';
import { parseApiError } from '#/util/parseApiError';
import { NETWORK_DISPLAY_NAMES } from '#/constants/audd';
import { INetworkRoute, IFiatRoute } from './auddSlice';
import { hasCryptoNetwork, isSelectable, selectFiatRouteSource } from './fiatRoute';

interface IMetaEntry {
  name: string;
  value: any;
}

function parseMeta(metaStr: string | null): IMetaEntry[] {
  if (!metaStr) return [];
  try {
    const parsed = JSON.parse(metaStr);
    if (Array.isArray(parsed)) {
      return parsed.flatMap((e) => {
        if (e == null || typeof e !== 'object') return [];
        if (e.name !== undefined && e.value !== undefined) {
          return [{ name: e.name, value: e.value }];
        }
        // Loose `{ key: value }` shape (e.g. `{ "validate_address": "on" }`)
        const keys = Object.keys(e);
        return keys.length === 1 ? [{ name: keys[0], value: e[keys[0]] }] : [];
      });
    }
    if (parsed && typeof parsed === 'object') {
      return Object.entries(parsed).map(([name, value]) => ({ name, value }));
    }
    return [];
  } catch {
    return [];
  }
}

function getMeta(entries: IMetaEntry[], key: string): string {
  return entries.find((e) => e.name === key)?.value || '';
}

function parseFiatDepositProperties(entries: IMetaEntry[]): { name: string; value: string }[] {
  const entry = entries.find((e) => e.name === 'fiat_deposit_properties');
  if (!entry) return [];

  let arr: any[];
  if (Array.isArray(entry.value)) {
    arr = entry.value;
  } else if (typeof entry.value === 'string') {
    try { arr = JSON.parse(entry.value); } catch { return []; }
  } else {
    return [];
  }

  return arr.filter((e: any) => e?.name !== undefined && e?.value !== undefined);
}

/**
 * The curated map wins so the networks already on screen keep the labels users know (`ripple` reads
 * as XRP, not as whatever the catalogue happens to be called). Anything new falls back to the
 * catalogue name Back Office stores on the route, and finally to the raw network code.
 *
 * Catalogue names are written like `Ethereum (Base)`, where the chain is the part in brackets —
 * cointrader unwraps them the same way in `paymentRouteMeta.cleanNetworkDisplayName`.
 */
function networkDisplayName(route: IPaymentsRoute): string {
  const network = route.crypto_network as string;
  const curated = NETWORK_DISPLAY_NAMES[network.toLowerCase()];
  if (curated) return curated;

  const catalogueName = route.crypto_network_name;
  if (catalogueName && catalogueName.toLowerCase() !== 'default') {
    const bracketed = catalogueName.match(/\(([^)]+)\)/);
    return (bracketed ? bracketed[1] : catalogueName).trim();
  }

  return network.charAt(0).toUpperCase() + network.slice(1);
}

function toNetworkRoute(route: IPaymentsRoute): INetworkRoute {
  const meta = parseMeta(route.meta);
  return {
    name: (route.crypto_network as string).toLowerCase(),
    displayName: networkDisplayName(route),
    addressTagType: route.crypto_address_tag_type || null,
    verificationType: route.verification_type || null,
    paymentRouteId: route.payment_route_id,
    pspServiceId: route.psp_service_id || null,
    depositEnabled: route.deposit_enabled === 'on',
    withdrawalEnabled: route.withdrawal_enabled === 'on',
    validateAddress: getMeta(meta, 'validate_address') === 'on',
  };
}

export const initializeWidget = createAsyncThunk(
  'audd/initializeWidget',
  async (_, thunkAPI) => {
    try {
      const { apiUrl, storedDataKey, currencyId, theme } = getUrlParams();

      // Apply theme immediately
      document.body.classList.value = `theme-${theme}`;

      // Initialize GQL client
      v4Service.initialize(apiUrl);

      // Auth: fetch token via storedDataKey
      const token = await v4Service.fetchToken(apiUrl, storedDataKey);
      v4Service.setToken(token);

      // Fetch the currency's payment routes, user verification and the deposits-without-KYC toggle in parallel
      const [routes, userVerification, allowDepositsWithoutKyc] = await Promise.all([
        v4Service.getPaymentRoutesByCurrency(currencyId),
        v4Service.getUserVerification(),
        v4Service.getAllowDepositsWithoutKyc(),
      ]);

      const selectable = routes.filter(isSelectable);
      const fiatRouteSource = selectFiatRouteSource(selectable);

      const fiatRoute: IFiatRoute | null = fiatRouteSource
        ? {
            paymentRouteId: fiatRouteSource.payment_route_id,
            pspServiceId: fiatRouteSource.psp_service_id || null,
            verificationType: fiatRouteSource.verification_type || null,
          }
        : null;

      // The fiat leg serves fiat only, so it is kept out of the network list even when it still
      // names a chain — offering it there would deposit crypto through the route the bank details
      // belong to. A chain reachable only through that route therefore has to get its own.
      const networkRoutes = selectable
        .filter((route) => route !== fiatRouteSource)
        .filter(hasCryptoNetwork)
        .map(toNetworkRoute);

      const meta = parseMeta(fiatRouteSource?.meta ?? null);

      return {
        fiatAccountName: getMeta(meta, 'fiat_account_name'),
        fiatBsb: getMeta(meta, 'fiat_bsb'),
        fiatAccountNumber: getMeta(meta, 'fiat_account_number'),
        fiatReference: getMeta(meta, 'fiat_reference'),
        fiatDepositProperties: fiatRouteSource?.fiat_deposit_properties?.length
          ? fiatRouteSource.fiat_deposit_properties
          : parseFiatDepositProperties(meta),
        fiatRoute,
        networkRoutes,
        kycStatus: userVerification.kyc_status,
        isUsing2fa: userVerification.is_using_2fa,
        feeGroupId: userVerification.fee_group_id,
        hasVirtualAccount: userVerification.has_virtual_account,
        allowDepositsWithoutKyc,
        isTravelRuleEnabled: userVerification.is_travel_rule_enabled,
        kycFirstName: userVerification.kyc_first_name,
        kycLastName: userVerification.kyc_last_name,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(parseApiError(error));
    }
  }
);

export const fetchWallets = createAsyncThunk(
  'audd/fetchWallets',
  async (currencyId: string, thunkAPI) => {
    try {
      return toWalletOptions(await v4Service.getWalletsPortfolio(), currencyId);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(parseApiError(error));
    }
  }
);

export const fetchDepositAddress = createAsyncThunk(
  'audd/fetchDepositAddress',
  async ({ currencyId, network, pspServiceId }: { currencyId: string; network: string; pspServiceId: string }, thunkAPI) => {
    try {
      const result = await v4Service.depositAddressCrypto(currencyId, network, pspServiceId);
      return {
        address: result.address,
        address_tag_type: result.address_tag_type || null,
        address_tag_value: result.address_tag_value || null,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(parseApiError(error));
    }
  }
);
